import { g } from '@hcie/core';
import { convertHslToHex, convertHexToRgba, RgbaColor } from '@hcie/shared';

/**
 * @file painting_tools.ts
 * @description Core Painting Tools providing basic drawing routines for pen, brush, eraser, and shapes.
 * Refactored to comply with AGENTS.md descriptive naming standards.
 */

/**
 * @function updateCyclicColor
 * @description Updates the global pen color if cyclic_color mode is enabled.
 */
export function updateCyclicColor(): void {
    if (!g.cyclic_color) return;
    g.pen_hue = (g.pen_hue + (g.cyclic_color_speed || 5)) % 360;
    g.pen_color = convertHslToHex(g.pen_hue, 100, 50);
    window.dispatchEvent(new CustomEvent('colorChanged', { 
        detail: { color: g.pen_color, source: 'cyclic' } 
    }));
}

/**
 * @function drawPen
 * @description Draws a stroke using the current pen settings.
 */
/** Returns effective pressure: 1.0 when pressure sensitivity is disabled, raw value when enabled. */
function getEffectivePressure(): number {
    if ((g as any).pressure_enabled === false) return 1.0;
    return (window as any)._currentPressure ?? 1.0;
}

export function drawPen(_e: MouseEvent, ctx: CanvasRenderingContext2D): void {
    const pressure = getEffectivePressure();
    const tip = g.pen_tip || 'round';
    
    if (tip === 'round') {
        updateCyclicColor();
        ctx.beginPath();
        // Dynamic size based on pressure
        const dynamicSize = Math.max(1, (g.pen_width || 1) * pressure);
        ctx.lineWidth = dynamicSize;
        ctx.strokeStyle = g.pen_color || "#000000";
        // Dynamic opacity based on pressure
        ctx.globalAlpha = (g.pen_opacity || 1.0) * (0.8 + pressure * 0.2); 
        ctx.lineCap = g.pen_cap || "round";
        ctx.lineJoin = g.pen_join || "round";
        ctx.moveTo(g.startX, g.startY);
        ctx.lineTo(g.pX, g.pY);
        ctx.stroke();
    } else {
        const dist = Math.hypot(g.pX - g.startX, g.pY - g.startY);
        const steps = Math.max(1, Math.ceil(dist / Math.max(1, g.pen_width / 4)));
        for (let i = 0; i <= steps; i++) {
            const x = g.startX + (g.pX - g.startX) * (i / steps);
            const y = g.startY + (g.pY - g.startY) * (i / steps);
            updateCyclicColor();
            drawPenTip(ctx, x, y);
        }
    }
    g.startX = g.pX;
    g.startY = g.pY;
}

/**
 * @function drawPenTip
 * @description Helper to draw a single stamp of the pen tip.
 */
function drawPenTip(ctx: CanvasRenderingContext2D, x: number, y: number) {
    const size = g.pen_width;
    const color = g.pen_color;
    const alpha = g.pen_opacity || 1.0;
    const tip = g.pen_tip || 'round';
    const angle = (g.pen_angle || 0) * (Math.PI / 180);

    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.fillStyle = color;
    ctx.translate(x, y);

    if (tip !== 'round' && tip !== 'soft') {
        ctx.rotate(angle);
    }

    if (tip === 'square') {
        ctx.fillRect(-size/2, -size/2, size, size);
    } else if (tip === 'ellipse') {
        ctx.beginPath();
        ctx.ellipse(0, 0, size/2, size/4, 0, 0, Math.PI * 2);
        ctx.fill();
    } else if (tip === 'soft') {
        const radius = size / 2;
        const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, radius);
        const rgba: RgbaColor = convertHexToRgba(color);
        gradient.addColorStop(0, `rgba(${rgba.red}, ${rgba.green}, ${rgba.blue}, 1)`);
        gradient.addColorStop(1, `rgba(${rgba.red}, ${rgba.green}, ${rgba.blue}, 0)`);
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(0, 0, radius, 0, Math.PI * 2);
        ctx.fill();
    } else if (tip === 'diamond') {
        ctx.beginPath();
        ctx.moveTo(0, -size/2);
        ctx.lineTo(size/2, 0);
        ctx.lineTo(0, size/2);
        ctx.lineTo(-size/2, 0);
        ctx.closePath();
        ctx.fill();
    } else if (tip === 'vertical') {
        ctx.fillRect(-1.5, -size/2, 3, size);
    } else if (tip === 'horizontal') {
        ctx.fillRect(-size/2, -1.5, size, 3);
    } else if (tip === 'star') {
        const spikes = 5;
        const outerRadius = size / 2;
        const innerRadius = size / 4;
        let rot = Math.PI / 2 * 3;
        let step = Math.PI / spikes;
        ctx.beginPath();
        ctx.moveTo(0, -outerRadius);
        for (let i = 0; i < spikes; i++) {
            ctx.lineTo(Math.cos(rot) * outerRadius, Math.sin(rot) * outerRadius);
            rot += step;
            ctx.lineTo(Math.cos(rot) * innerRadius, Math.sin(rot) * innerRadius);
            rot += step;
        }
        ctx.lineTo(0, -outerRadius);
        ctx.closePath();
        ctx.fill();
    } else {
        ctx.beginPath();
        ctx.arc(0, 0, size/2, 0, Math.PI * 2);
        ctx.fill();
    }
    ctx.restore();
}

/**
 * @function drawLine
 * @description Draws a straight line between start and current point.
 */
export function drawLine(_e: MouseEvent, ctx: CanvasRenderingContext2D): void {
    ctx.beginPath();
    ctx.lineWidth = g.pen_width;
    ctx.strokeStyle = g.pen_color;
    ctx.globalAlpha = g.pen_opacity || 1.0;
    ctx.lineCap = g.pen_cap || "round";
    ctx.lineJoin = g.pen_join || "round";
    ctx.moveTo(g.startX, g.startY);
    ctx.lineTo(g.pX, g.pY);
    ctx.stroke();
    ctx.closePath();
}

/**
 * @function drawRect
 * @description Draws a rectangle.
 */
export function drawRect(ctx: CanvasRenderingContext2D, x1: number, y1: number, x2: number, y2: number): void {
    ctx.beginPath();
    ctx.lineWidth = g.pen_width;
    ctx.strokeStyle = g.pen_color;
    ctx.fillStyle = g.pen_secondary_color;
    ctx.globalAlpha = g.pen_opacity || 1.0;
    ctx.rect(x1, y1, x2 - x1, y2 - y1);
    if (g.shape_fill) ctx.fill();
    ctx.stroke();
    ctx.closePath();
}

/**
 * @function drawCircle
 * @description Draws a circle centered at start point.
 */
export function drawCircle(_e: MouseEvent, ctx: CanvasRenderingContext2D): void {
    ctx.beginPath();
    ctx.lineWidth = g.pen_width;
    ctx.strokeStyle = g.pen_color;
    ctx.fillStyle = g.pen_secondary_color;
    ctx.globalAlpha = g.pen_opacity || 1.0;
    const radius = Math.hypot(g.pX - g.startX, g.pY - g.startY);
    ctx.arc(g.startX, g.startY, radius, 0, 2 * Math.PI);
    if (g.shape_fill) ctx.fill();
    ctx.stroke();
    ctx.closePath();
}

/**
 * @function drawBrush
 * @description Draws textured strokes using various artistic brush styles.
 */
export function drawBrush(ctx: CanvasRenderingContext2D, x: number, y: number): void {
    const pressure = getEffectivePressure();
    const size     = (g.pen_width || 20) * (0.4 + pressure * 0.6); // 40–100% of base size
    const hardness = (g.brush_hardness !== undefined) ? g.brush_hardness : 0.5;
    const color = g.pen_color;
    const flow = (g.brush_flow !== undefined) ? g.brush_flow : 1.0;
    const style = g.brush_style || 'default';

    updateCyclicColor();
    ctx.save();
    ctx.globalAlpha = (g.pen_opacity || 1.0) * flow * (0.7 + pressure * 0.3);

    if (style === 'oil') {
        const bristleCount = Math.max(5, Math.floor(size / 3));
        const rgba: RgbaColor = convertHexToRgba(color);
        for (let i = 0; i < bristleCount; i++) {
            const offsetX = (Math.random() - 0.5) * size;
            const offsetY = (Math.random() - 0.5) * size;
            const bristleRadius = (Math.random() * 2 + 1) * (size / 20);
            const red: number = Math.min(255, Math.max(0, rgba.red + (Math.random() - 0.5) * 20));
            const green: number = Math.min(255, Math.max(0, rgba.green + (Math.random() - 0.5) * 20));
            const blue: number = Math.min(255, Math.max(0, rgba.blue + (Math.random() - 0.5) * 20));
            ctx.fillStyle = `rgba(${red}, ${green}, ${blue}, ${0.4 + Math.random() * 0.4})`;
            ctx.beginPath();
            ctx.arc(x + offsetX, y + offsetY, bristleRadius, 0, Math.PI * 2);
            ctx.fill();
        }
    } else if (style === 'leaf') {
        const angle = Math.random() * Math.PI * 2;
        ctx.translate(x, y);
        ctx.rotate(angle);
        ctx.fillStyle = color;
        const width = size;
        const height = size * 0.6;
        ctx.beginPath();
        ctx.moveTo(0, -height/2);
        ctx.bezierCurveTo(width/2, -height/2, width/2, height/2, 0, height/2);
        ctx.bezierCurveTo(-width/2, height/2, -width/2, -height/2, 0, -height/2);
        ctx.fill();
        ctx.strokeStyle = 'rgba(0,0,0,0.2)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(0, -height/2);
        ctx.lineTo(0, height/2);
        ctx.stroke();
    } else if (style === 'rock') {
        const sides = 5 + Math.floor(Math.random() * 3);
        const radius = size / 2;
        ctx.fillStyle = color;
        ctx.beginPath();
        for (let i = 0; i < sides; i++) {
            const a = (i / sides) * Math.PI * 2;
            const r = radius * (0.7 + Math.random() * 0.3);
            const px = x + Math.cos(a) * r;
            const py = y + Math.sin(a) * r;
            if (i === 0) ctx.moveTo(px, py);
            else ctx.lineTo(px, py);
        }
        ctx.closePath();
        ctx.fill();
        ctx.fillStyle = 'rgba(255,255,255,0.1)';
        ctx.beginPath();
        ctx.arc(x - radius/3, y - radius/3, radius/4, 0, Math.PI * 2);
        ctx.fill();
    } else if (style === 'charcoal') {
        const radius = size / 2;
        const particleCount = Math.max(12, Math.floor(size));
        const rgba: RgbaColor = convertHexToRgba(color);
        for (let i = 0; i < particleCount; i++) {
            const r = Math.sqrt(Math.random()) * radius;
            const theta = Math.random() * 2 * Math.PI;
            const px = x + r * Math.cos(theta);
            const py = y + r * Math.sin(theta);
            const particleSize = (Math.random() * 2.5 + 0.5) * (size / 20);
            const particleAlpha = 0.05 + Math.random() * 0.35;
            ctx.fillStyle = `rgba(${rgba.red}, ${rgba.green}, ${rgba.blue}, ${particleAlpha})`;
            ctx.beginPath();
            ctx.arc(px, py, particleSize, 0, Math.PI * 2);
            ctx.fill();
        }
    } else if (style === 'meadow') {
        const bladeCount = 3;
        ctx.strokeStyle = color;
        ctx.lineCap = 'round';
        for (let i = 0; i < bladeCount; i++) {
            const offsetX = (Math.random() - 0.5) * size;
            const bladeHeight = size * (0.5 + Math.random() * 0.5);
            const lean = (Math.random() - 0.5) * 10;
            ctx.lineWidth = 1 + Math.random() * 2;
            ctx.beginPath();
            ctx.moveTo(x + offsetX, y);
            ctx.quadraticCurveTo(x + offsetX + lean, y - bladeHeight/2, x + offsetX + lean*2, y - bladeHeight);
            ctx.stroke();
        }
    } else if (style === 'wood') {
        const width = size * 1.5;
        const height = size * 0.4;
        ctx.translate(x, y);
        ctx.rotate((Math.random() - 0.5) * 0.2);
        const rgba: RgbaColor = convertHexToRgba(color);
        ctx.fillStyle = `rgba(${rgba.red}, ${rgba.green}, ${rgba.blue}, 0.8)`;
        ctx.fillRect(-width/2, -height/2, width, height);
        ctx.strokeStyle = 'rgba(0,0,0,0.15)';
        ctx.lineWidth = 1;
        for (let i = 0; i < 3; i++) {
            const gy = (Math.random() - 0.5) * height;
            ctx.beginPath();
            ctx.moveTo(-width/2, gy);
            ctx.bezierCurveTo(-width/4, gy + 2, width/4, gy - 2, width/2, gy);
            ctx.stroke();
        }
    } else {
        const pressure = getEffectivePressure();
        // Dynamic radius based on pressure
        const dynamicRadius = (size / 2) * (0.5 + pressure * 0.5);
        const radius = dynamicRadius;
        const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius);
        const rgba: RgbaColor = convertHexToRgba(color);
        const centerColor = `rgba(${rgba.red}, ${rgba.green}, ${rgba.blue}, 1)`;
        const edgeColor = `rgba(${rgba.red}, ${rgba.green}, ${rgba.blue}, 0)`;
        gradient.addColorStop(0, centerColor);
        if (hardness > 0) {
            gradient.addColorStop(Math.min(0.99, hardness), centerColor);
        }
        gradient.addColorStop(1.0, edgeColor);
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(x, y, radius, 0, Math.PI * 2);
        ctx.fill();
    }
    ctx.restore();
}

/**
 * @function drawEraser
 * @description Draws an eraser stroke following the user's path.
 */
export function drawEraser(ctx: CanvasRenderingContext2D, xStart: number, yStart: number, xEnd: number, yEnd: number): void {
    const pressure = getEffectivePressure();
    ctx.save();
    ctx.globalCompositeOperation = 'source-over';
    const length = Math.hypot(xEnd - xStart, yEnd - yStart);
    let numSteps = 1;
    if (length >= 1) {
        numSteps = Math.floor(length / (g.pen_width / 4.0)) + 1;
    }
    for (let i = 0; i <= numSteps; i++) {
        const t = i / numSteps;
        const cx = xStart * (1.0 - t) + xEnd * t;
        const cy = yStart * (1.0 - t) + yEnd * t;
        // Dynamic radius for eraser based on pressure
        const dynamicRadius = (g.pen_width / 2) * (0.8 + pressure * 0.2);
        const radius = dynamicRadius;
        const gradient = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius);
        const centerColor = `rgba(0, 0, 0, ${g.pen_opacity || 1.0})`;
        const edgeColor = 'rgba(0, 0, 0, 0)';
        gradient.addColorStop(0, centerColor);
        const hardness = (g.brush_hardness !== undefined) ? g.brush_hardness : 0.8;
        if (hardness > 0) gradient.addColorStop(Math.min(0.99, hardness), centerColor);
        gradient.addColorStop(1.0, edgeColor);
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(cx, cy, radius, 0, Math.PI * 2);
        ctx.fill();
    }
    ctx.restore();
}

/**
 * @function drawRoundedRect
 * @description Draws a rectangle with rounded corners.
 */
export function drawRoundedRect(ctx: CanvasRenderingContext2D, x1: number, y1: number, x2: number, y2: number): void {
    const x = Math.min(x1, x2);
    const y = Math.min(y1, y2);
    const width = Math.abs(x2 - x1);
    const height = Math.abs(y2 - y1);
    let radius = g.round_rect_corner_radius || 10;
    if (radius > width / 2) radius = width / 2;
    if (radius > height / 2) radius = height / 2;
    ctx.beginPath();
    ctx.lineWidth = g.pen_width;
    ctx.strokeStyle = g.pen_color;
    ctx.fillStyle = g.pen_secondary_color;
    ctx.globalAlpha = g.pen_opacity || 1.0;
    if ((ctx as any).roundRect) {
        (ctx as any).roundRect(x, y, width, height, radius);
    } else {
        ctx.moveTo(x + radius, y);
        ctx.lineTo(x + width - radius, y);
        ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
        ctx.lineTo(x + width, y + height - radius);
        ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
        ctx.lineTo(x + radius, y + height);
        ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
        ctx.lineTo(x, y + radius);
        ctx.quadraticCurveTo(x, y, x + radius, y);
    }
    ctx.closePath();
    if (g.shape_fill) ctx.fill();
    ctx.stroke();
}

/**
 * @function drawEllipse
 * @description Draws an ellipse matching the given bounds.
 */
export function drawEllipse(ctx: CanvasRenderingContext2D, x1: number, y1: number, x2: number, y2: number): void {
    ctx.beginPath();
    ctx.lineWidth = g.pen_width;
    ctx.strokeStyle = g.pen_color;
    ctx.fillStyle = g.pen_secondary_color;
    ctx.globalAlpha = g.pen_opacity || 1.0;
    ctx.ellipse((x1 + x2) / 2, (y1 + y2) / 2, Math.abs(x2 - x1) / 2, Math.abs(y2 - y1) / 2, 0, 0, Math.PI * 2);
    if (g.shape_fill) ctx.fill();
    ctx.stroke();
    ctx.closePath();
}

/**
 * @function eyeDropperColor
 * @description Picks the hex color from a canvas pixel at (x, y).
 */
export function eyeDropperColor(x: number, y: number, ctx: CanvasRenderingContext2D): string | null {
    try {
        const imageData: ImageData = ctx.getImageData(x, y, 1, 1);
        const [red, green, blue, alpha]: Uint8ClampedArray = imageData.data;
        if (alpha === 0) return null;
        const toHex = (n: number): string => n.toString(16).padStart(2, '0');
        return `#${toHex(red)}${toHex(green)}${toHex(blue)}`;
    } catch (error) {
        console.error('[EyeDropper] Error picking color:', error);
        return null;
    }
}

/**
 * @function drawSpotHealing
 * @description Heals a spot by blending surrounding pixel data into the brushed area.
 * A simplified version using neighborhood averaging.
 */
export function drawSpotHealing(ctx: CanvasRenderingContext2D, x: number, y: number): void {
    const size = g.pen_width || 20;
    const radius = size / 2;
    
    // 1. Capture neighborhood data (around the brush)
    // We sample a slightly larger area to get context
    const sampleSize = Math.ceil(size * 1.5);
    const sampleX = Math.round(x - sampleSize / 2);
    const sampleY = Math.round(y - sampleSize / 2);
    
    try {
        const imageData = ctx.getImageData(sampleX, sampleY, sampleSize, sampleSize);
        const data = imageData.data;
        
        // 2. Simple Median/Average Filter logic for the center spot
        // We'll calculate the average of the "outer" rim of our sample
        let r = 0, g_ = 0, b = 0, a = 0, count = 0;
        
        for (let i = 0; i < data.length; i += 4) {
            // Only sample from the edges of our captured box to avoid the "spot" itself
            const px = (i / 4) % sampleSize;
            const py = Math.floor((i / 4) / sampleSize);
            const distToCenter = Math.hypot(px - sampleSize/2, py - sampleSize/2);
            
            if (distToCenter > radius && distToCenter < sampleSize/2) {
                r += data[i];
                g_ += data[i + 1];
                b += data[i + 2];
                a += data[i + 3];
                count++;
            }
        }
        
        if (count > 0) {
            const avgR = Math.round(r / count);
            const avgG = Math.round(g_ / count);
            const avgB = Math.round(b / count);
            const avgA = (a / count) / 255;
            
            // 3. Paint the averaged color with a soft radial gradient
            const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius);
            gradient.addColorStop(0, `rgba(${avgR}, ${avgG}, ${avgB}, ${avgA})`);
            gradient.addColorStop(1, `rgba(${avgR}, ${avgG}, ${avgB}, 0)`);
            
            ctx.save();
            ctx.fillStyle = gradient;
            ctx.beginPath();
            ctx.arc(x, y, radius, 0, Math.PI * 2);
            ctx.fill();
            ctx.restore();
        }
    } catch (e) {
        // Fallback or ignore if out of bounds
    }
}

export function drawCloneStamp(ctx: CanvasRenderingContext2D, x: number, y: number): void {
    if (!g.cloneSourcePoint) return;
    
    const size = g.pen_width || 20;
    const radius = size / 2;
    const opacity = g.pen_opacity || 1.0;
    
    const dx = x - (g as any).mouseDownX;
    const dy = y - (g as any).mouseDownY;
    
    const sampleX = g.cloneSourcePoint.x + dx;
    const sampleY = g.cloneSourcePoint.y + dy;

    ctx.save();
    
    // Create soft brush clip effect
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.clip();
    
    ctx.globalAlpha = opacity;
    
    // We draw from the source canvas (which is the same as ctx.canvas)
    // To avoid "trails" while drawing on tempCanvas, we should sample from the original layer canvas
    // Or just sample from display canvas if we want merged result.
    // For now, let's assume we sample from the current visible result.
    const drawingCanvas = document.getElementById('drawingCanvas') as HTMLCanvasElement;
    if (drawingCanvas) {
        ctx.drawImage(
            drawingCanvas,
            sampleX - radius, sampleY - radius, size, size, // Source rect
            x - radius, y - radius, size, size           // Dest rect
        );
    }
    
    ctx.restore();
}

// Global exposure
(window as any).updateCyclicColor = updateCyclicColor;
(window as any).drawPen = drawPen;
(window as any).drawLine = drawLine;
(window as any).drawRect = drawRect;
(window as any).drawCircle = drawCircle;
(window as any).drawBrush = drawBrush;
(window as any).drawEraser = drawEraser;
(window as any).drawRoundedRect = drawRoundedRect;
(window as any).drawEllipse = drawEllipse;
(window as any).eyeDropperColor = eyeDropperColor;
(window as any).drawSpotHealing = drawSpotHealing;
(window as any).drawCloneStamp = drawCloneStamp;
